// Hash-based router. Pages register themselves at module-load time via
// `registerPage(...)` and the chrome owns the mount/unmount lifecycle.
//
// The router exposes `navigate(name)` to the rest of the chrome and to
// pages (via PageContext), and listens for `hashchange` so back/forward
// buttons work too.

import { forceClearScrollLock } from "@tkos/shared-ui";
import type { Page, PageCleanup, PageContext } from "./types";

// ── Registry ────────────────────────────────────────────────────────────────

const pages = new Map<string, Page>();
const order: string[] = []; // preserves insertion order for the sidebar

/** Register a page. Idempotent — calling twice with the same name overwrites. */
export function registerPage(page: Page): void {
  if (!pages.has(page.name)) order.push(page.name);
  pages.set(page.name, page);
}

/** All registered pages in registration order (for the sidebar to render). */
export function listPages(): readonly Page[] {
  return order.map((n) => pages.get(n)!).filter(Boolean);
}

export function getPage(name: string): Page | undefined {
  return pages.get(name);
}

/**
 * Replace the sidebar order with `canonical`, filtered to currently
 * registered pages. Any registered pages not in `canonical` are appended
 * at the end (preserving their existing relative order). Useful when real
 * pages and stub pages are registered in different relative orders due to
 * ES-module import-hoisting — call this once at the bottom of the page
 * registry module to enforce a deterministic sidebar layout.
 */
export function setOrder(canonical: string[]): void {
  const seen = new Set<string>();
  const next: string[] = [];
  for (const name of canonical) {
    if (pages.has(name) && !seen.has(name)) {
      next.push(name);
      seen.add(name);
    }
  }
  for (const name of order) {
    if (!seen.has(name)) {
      next.push(name);
      seen.add(name);
    }
  }
  order.length = 0;
  order.push(...next);
}

// ── Mount lifecycle ─────────────────────────────────────────────────────────

interface RouterRuntime {
  container: HTMLElement;
  ctx: PageContext;
  defaultPage: string;
  currentName: string | null;
  /** Last params we mounted with — used to skip no-op re-mounts. */
  currentParamsKey: string;
  currentCleanup: PageCleanup | null;
}

let runtime: RouterRuntime | null = null;
const navListeners = new Set<(name: string) => void>();

/** Subscribe to navigation events (sidebar uses this to update active state). */
export function onNavigate(fn: (name: string) => void): () => void {
  navListeners.add(fn);
  return () => navListeners.delete(fn);
}

export function getCurrent(): string | null {
  return runtime?.currentName ?? null;
}

/** Parsed `location.hash`. */
interface ParsedHash {
  name: string | null;
  params: Record<string, string>;
}

/** Parse the page name + ?key=value query out of `location.hash`.
 *
 *  Examples:
 *    `#/foo`               -> { name: 'foo', params: {} }
 *    `#/crm?focus=lead-1`  -> { name: 'crm', params: { focus: 'lead-1' } }
 *    `#/crm?focus=Jane%20Doe`       -> decoded "Jane Doe"
 */
function parseHash(): ParsedHash {
  const h = window.location.hash || "";
  const m = h.match(/^#\/([a-z0-9-]+)(?:\?(.*))?$/i);
  if (!m) return { name: null, params: {} };
  const name = (m[1] ?? "").toLowerCase() || null;
  const params: Record<string, string> = {};
  const qs = m[2];
  if (qs) {
    for (const pair of qs.split("&")) {
      if (!pair) continue;
      const eq = pair.indexOf("=");
      const k = eq >= 0 ? pair.slice(0, eq) : pair;
      const v = eq >= 0 ? pair.slice(eq + 1) : "";
      if (!k) continue;
      try {
        params[decodeURIComponent(k)] = decodeURIComponent(v);
      } catch {
        params[k] = v;
      }
    }
  }
  return { name, params };
}

function serialiseParams(params: Record<string, string> | undefined): string {
  if (!params) return "";
  const entries = Object.entries(params).filter(([, v]) => v != null && v !== "");
  if (entries.length === 0) return "";
  return (
    "?" +
    entries
      .map(([k, v]) => encodeURIComponent(k) + "=" + encodeURIComponent(v))
      .join("&")
  );
}

function writeHash(name: string, params?: Record<string, string>): void {
  const target = `#/${name}${serialiseParams(params)}`;
  if (window.location.hash !== target) {
    // Use replaceState when we're correcting an unknown route, but for normal
    // navigation we want the back button to work — so just set the hash.
    window.location.hash = target;
  }
}

function resolvePageName(requested: string | null): string {
  if (requested && pages.has(requested)) return requested;
  if (runtime && pages.has(runtime.defaultPage)) return runtime.defaultPage;
  // Fallback: first registered page.
  return order[0] ?? "dashboard";
}

function mountPage(name: string, params: Record<string, string>): void {
  if (!runtime) return;

  // Backstop for the scroll-lock: guarantee the page is scrollable after any
  // navigation, even if an overlay was torn down mid-close without unlocking.
  // (Issue: "page sticks, can't scroll, fixed by refresh".)
  forceClearScrollLock();

  // Tear down the previous page first.
  if (runtime.currentCleanup) {
    try {
      runtime.currentCleanup();
    } catch (err) {
      console.warn(`[router] cleanup of "${runtime.currentName}" threw:`, err);
    }
    runtime.currentCleanup = null;
  }

  // Empty container.
  runtime.container.replaceChildren();

  const page = pages.get(name);
  if (!page) {
    runtime.container.innerHTML = `<div class="tkos-page-missing">No page registered for "${name}".</div>`;
    runtime.currentName = name;
    return;
  }

  // Build the per-mount context. Inherits everything from the shell's
  // shared ctx and tacks on the parsed `params`.
  const mountCtx: PageContext = { ...runtime.ctx, params };

  let cleanup: PageCleanup;
  try {
    cleanup = page.mount(runtime.container, mountCtx);
    // Premium polish: replay the page-enter fade on each navigation (CSS-gated
    // by prefers-reduced-motion). Re-trigger the animation by toggling the class.
    runtime.container.classList.remove("tkos-page-enter");
    void runtime.container.offsetWidth;
    runtime.container.classList.add("tkos-page-enter");
  } catch (err) {
    console.error(`[router] mount of "${name}" threw:`, err);
    runtime.container.innerHTML = `<div class="tkos-page-error">Page "${name}" failed to load. See console.</div>`;
    runtime.currentName = name;
    return;
  }

  runtime.currentCleanup = typeof cleanup === "function" ? cleanup : () => {};
  runtime.currentName = name;
  runtime.currentParamsKey = JSON.stringify(params);
  for (const fn of navListeners) fn(name);
}

/** Programmatic navigation. Optional `params` are serialised into the hash
 *  (e.g. `navigate("crm", { focus: "lead-1" })` → `#/crm?focus=lead-1`).
 *  Pages receive these in `ctx.params` when they mount. */
export function navigate(name: string, params?: Record<string, string>): void {
  if (!runtime) return;
  const resolved = resolvePageName(name);
  // Setting the hash will fire `hashchange`, which mounts the page.
  // If the hash is already correct (e.g. user clicked the active page),
  // mount directly so the page can re-render with new params.
  const current = parseHash();
  const sameRoute =
    current.name === resolved &&
    JSON.stringify(current.params) === JSON.stringify(params ?? {});
  if (sameRoute) {
    mountPage(resolved, params ?? {});
  } else {
    writeHash(resolved, params);
  }
}

/**
 * Start the router. Mounts the page for the current hash (or `defaultPage`
 * if the hash is empty/unknown) and begins listening for hash changes.
 *
 * Returns a teardown function that detaches listeners and unmounts.
 */
export function startRouter(opts: {
  container: HTMLElement;
  ctx: PageContext;
  defaultPage: string;
}): () => void {
  runtime = {
    container: opts.container,
    ctx: opts.ctx,
    defaultPage: opts.defaultPage,
    currentName: null,
    currentParamsKey: "",
    currentCleanup: null,
  };

  const onHashChange = (): void => {
    const parsed = parseHash();
    const resolved = resolvePageName(parsed.name);
    if (parsed.name !== resolved) {
      // Unknown / missing hash → quietly correct it without adding history.
      const target = `#/${resolved}${serialiseParams(parsed.params)}`;
      window.history.replaceState(null, "", `${window.location.pathname}${window.location.search}${target}`);
    }
    const paramsKey = JSON.stringify(parsed.params);
    if (resolved !== runtime?.currentName || paramsKey !== runtime?.currentParamsKey) {
      mountPage(resolved, parsed.params);
    }
  };

  window.addEventListener("hashchange", onHashChange);

  // Initial mount.
  onHashChange();

  return () => {
    window.removeEventListener("hashchange", onHashChange);
    if (runtime?.currentCleanup) {
      try {
        runtime.currentCleanup();
      } catch {
        /* ignore */
      }
    }
    runtime = null;
  };
}
